import { Box, Button, Stack, Typography, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  useNotificationSettingsQuery,
  useConnectTelegramMutation,
} from '../../../hooks/useNotificationSettings';
import { getApiErrorMessage } from '../../../common/utils/getApiErrorMessage';
import { openLinkInNewTab } from '../../../common/utils/openLinkInNewTab';

/**
 * Compact banner inviting users who have NOT linked Telegram to connect the bot
 * so they receive charging notifications. Renders nothing while loading or once
 * the account is already linked (the query refetches on window focus, so the
 * banner disappears automatically after the user links in the bot and returns).
 */
export const TelegramNotifyPrompt = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useNotificationSettingsQuery();
  const connect = useConnectTelegramMutation();

  if (isLoading || !data || data.telegramLinked) return null;

  // Open the tab synchronously inside the click gesture (via openLinkInNewTab)
  // and redirect it once the deep link resolves, so the popup isn't blocked.
  const handleConnect = () =>
    openLinkInNewTab(async () => (await connect.mutateAsync()).deepLink);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={(theme: Theme) => ({
        width: '100%',
        padding: '12px 14px',
        borderRadius: '12px',
        backgroundColor: theme.palette.gray[300],
        border: '1px solid rgba(255, 255, 255, 0.12)',
      })}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: '#fff',
          flexShrink: 0,
        }}
      >
        <img
          src="/telegram-logo.png"
          alt=""
          width={24}
          height={24}
          style={{ display: 'block' }}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: 'common.white', lineHeight: 1.3 }}
        >
          {t('orderPage.telegramNotify.title')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'gray.100', lineHeight: 1.35 }}>
          {t('orderPage.telegramNotify.subtitle')}
        </Typography>
        {connect.isError && (
          <Typography sx={{ fontSize: 12, color: 'error.main', mt: 0.5 }}>
            {getApiErrorMessage(connect.error) ?? t('orderPage.telegramNotify.error')}
          </Typography>
        )}
      </Box>

      <Button
        variant="contained"
        color="primary"
        size="small"
        disabled={connect.isPending}
        onClick={handleConnect}
        sx={{ flexShrink: 0, borderRadius: '10px', fontWeight: 600 }}
      >
        {connect.isPending
          ? t('orderPage.telegramNotify.connecting')
          : t('orderPage.telegramNotify.connect')}
      </Button>
    </Stack>
  );
};
